"use strict";
/**
 * 资料下载与询盘的契约。
 *
 * 放在 `contracts` 而不是各自应用里，是因为这条链路上有**三个进程**
 * （控制台填表、平台校验落库、站点前台渲染与转发），三家对"邮箱最长多少、
 * 哪些 MIME 能传、留言超长怎么办"必须用同一份数。分开写的结果不是报错，
 * 而是"前台填得进去、后台存不下"这种只有客户会遇到的故障。
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.LEAD_RATE_WINDOW_MS = exports.LEAD_RATE_LIMIT_GLOBAL = exports.LEAD_RATE_LIMIT = exports.DOWNLOAD_TOKEN_TTL_SECONDS = exports.MAX_RESOURCE_BYTES = exports.RESOURCE_MIME_EXT = exports.LEAD_FIELD_LIMITS = exports.LEAD_STATUS_LABELS = exports.LEAD_STATUSES = exports.LEAD_KIND_LABELS = exports.LEAD_KINDS = void 0;
exports.isResourceMime = isResourceMime;
exports.normalizeLeadFields = normalizeLeadFields;
exports.formatFileSize = formatFileSize;
exports.formatLeadTime = formatLeadTime;
exports.leadsToCsv = leadsToCsv;
const platform_1 = require("./platform");
/** 询盘来源。两种走同一条管道，处置流程一致 */
exports.LEAD_KINDS = ['resource', 'contact'];
exports.LEAD_KIND_LABELS = {
    resource: '资料下载',
    contact: '联系表单',
};
exports.LEAD_STATUSES = ['new', 'read', 'archived', 'spam'];
exports.LEAD_STATUS_LABELS = {
    new: '未读',
    read: '已读',
    archived: '已归档',
    spam: '垃圾',
};
/**
 * 各字段长度上限。
 *
 * 上限不是为了防攻击（限流管那个），而是为了让「导出 CSV 再导入 CRM」
 * 不会因为某条 8000 字的留言把列宽撑爆。真实询盘的留言很少超过 1000 字。
 */
exports.LEAD_FIELD_LIMITS = {
    email: 200,
    name: 80,
    company: 120,
    country: 60,
    phone: 40,
    message: 2000,
    pagePath: 500,
    referrer: 500,
    utm: 120,
    userAgent: 400,
};
/**
 * 允许上传的资料类型。
 *
 * 只收这五种：外贸场景下的产品目录、认证证书、报价单、图纸包。
 * 刻意不收 HTML/SVG（可以被当成页面执行，等于在我们域名下挂了一个任意页面）
 * 与可执行文件（发给买家的文件被邮箱判毒，吃亏的是客户的域名信誉）。
 */
exports.RESOURCE_MIME_EXT = {
    'application/pdf': 'pdf',
    'application/zip': 'zip',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
};
function isResourceMime(value) {
    return Object.prototype.hasOwnProperty.call(exports.RESOURCE_MIME_EXT, value);
}
/**
 * 单份资料的大小上限。
 *
 * 比图片（20MB）宽，因为带图的目录 PDF 很容易过 20MB；而下载走 302 到对象存储，
 * 不经过我们的机器，所以这个数字不影响服务器带宽，只影响客户上传时的等待。
 */
exports.MAX_RESOURCE_BYTES = 50 * 1024 * 1024;
/**
 * 下载令牌寿命。
 *
 * 10 分钟：够一个买家点开、被浏览器拦截一次再点一次，又短到把地址贴到群里
 * 第二天就失效。它不是防盗措施（PDF 下载后本来就能转发），而是让"不填邮箱
 * 拿不到可长期使用的直链"这句话成立。
 */
exports.DOWNLOAD_TOKEN_TTL_SECONDS = 10 * 60;
/**
 * 同一 IP 在**同一个站点**窗口内允许提交的询盘数。
 *
 * 不设 1：外贸买家同一公司多人来问是正常的；也不能按 IP 全局算 ——
 * 展会现场十几台设备连同一个 WiFi，各看各的站，按全局算的话第 6 个人开始
 * 整片 429，而那正是外贸站最典型的获客场景。
 */
exports.LEAD_RATE_LIMIT = 5;
/**
 * 同一 IP 在窗口内**跨所有站点**的上限。
 *
 * 按站点分桶后，一个 IP 理论上能对着几百个站各刷 5 条。这条粗粒度上限专门堵它，
 * 取值要比"正常访客"宽得多（10 分钟 30 条 = 6 个站点的满额），
 * 正常使用碰不到，脚本扫表单会撞上。
 */
exports.LEAD_RATE_LIMIT_GLOBAL = 30;
exports.LEAD_RATE_WINDOW_MS = 10 * 60 * 1000;
function text(value, max) {
    if (typeof value !== 'string')
        return '';
    // 去控制字符：留言里夹 \u0000 会让 Postgres 的 text 列直接报错
    // （"unsupported Unicode escape sequence"），而那条错误发生在 insert 里，
    // 看起来跟表单毫无关系。
    // 这里**刻意**匹配控制字符区间 —— no-control-regex 守的是"误把控制字符
    // 当普通字符"，而这一行正是要精确地删掉它们，所以按规则的本意豁免。
    // eslint-disable-next-line no-control-regex
    const cleaned = value.replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f]/g, '');
    return cleaned.trim().slice(0, max);
}
/**
 * 归一化。**只做裁剪与去空，不做合法性判断** —— 邮箱有没有 @ 由平台判，
 * 因为只有平台能顺带做限流与"这个邮箱是不是已经来过"。
 */
function normalizeLeadFields(input) {
    return {
        email: text(input.email, exports.LEAD_FIELD_LIMITS.email).toLowerCase(),
        name: text(input.name, exports.LEAD_FIELD_LIMITS.name),
        company: text(input.company, exports.LEAD_FIELD_LIMITS.company),
        country: text(input.country, exports.LEAD_FIELD_LIMITS.country),
        phone: text(input.phone, exports.LEAD_FIELD_LIMITS.phone),
        message: text(input.message, exports.LEAD_FIELD_LIMITS.message),
        locale: text(input.locale, 12),
        pagePath: text(input.pagePath, exports.LEAD_FIELD_LIMITS.pagePath),
        referrer: text(input.referrer, exports.LEAD_FIELD_LIMITS.referrer),
        utmSource: text(input.utmSource, exports.LEAD_FIELD_LIMITS.utm),
        utmMedium: text(input.utmMedium, exports.LEAD_FIELD_LIMITS.utm),
        utmCampaign: text(input.utmCampaign, exports.LEAD_FIELD_LIMITS.utm),
    };
}
/** 人类可读的文件大小。列表页与邮件里共用，避免两处四舍五入到不同结果 */
function formatFileSize(bytes) {
    if (!Number.isFinite(bytes) || bytes <= 0)
        return '0 KB';
    if (bytes < 1024 * 1024)
        return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
function pad(value) {
    return String(value).padStart(2, '0');
}
/** 询盘列表用的时间格式：到分钟，带年份（跟进记录要能隔年回看） */
function formatLeadTime(iso) {
    const at = new Date(iso);
    if (Number.isNaN(at.getTime()))
        return '';
    return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())} ${pad(at.getHours())}:${pad(at.getMinutes())}`;
}
/**
 * 导出询盘 CSV。
 *
 * 列的顺序按"拿到表先干什么"排：先看是谁、怎么联系，再看他要什么、
 * 从哪来。来源页与 UTM 放最后 —— 它们只在复盘渠道时才有用。
 */
function leadsToCsv(rows) {
    const header = [
        '时间',
        '来源',
        '资料',
        '邮箱',
        '姓名',
        '公司',
        '国家',
        '电话',
        '留言',
        '语言',
        '来源页',
        '引用页',
        'UTM 来源',
        'UTM 媒介',
        'UTM 活动',
        '状态',
    ];
    const lines = rows.map((row) => [
        formatLeadTime(row.createdAt),
        exports.LEAD_KIND_LABELS[row.kind],
        row.resourceName ?? '',
        row.email,
        row.name,
        row.company,
        row.country,
        row.phone,
        row.message,
        row.locale,
        row.pagePath,
        row.referrer,
        row.utmSource,
        row.utmMedium,
        row.utmCampaign,
        exports.LEAD_STATUS_LABELS[row.status],
    ]
        .map(platform_1.csvCell)
        .join(','));
    return [header.map(platform_1.csvCell).join(','), ...lines].join('\r\n');
}
//# sourceMappingURL=leads.js.map